import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Check } from "lucide-react";
import { Reveal, SectionHeading, EASE } from "@/components/Reveal";

const IMG = {
  cockpit: "/images/panel-interior.jpg",
  suv: "/images/panel-power.jpg",
  rear: "/images/panel-anywhere.jpg",
  interior: "/images/panel-interior.jpg",
  detail: "/images/seats.jpg",
  hero: "/images/aero-hero.jpg",
  offroad: "/images/panel-offroad.jpg",
  highway: "/images/panel-anywhere.jpg",
};

const SECTIONS = [
  {
    id: "diesel",
    num: "01",
    title: "Powerful Diesel Engine",
    hindi: "ताकतवर डीज़ल इंजन",
    body: "High torque, high efficiency. 2.0L / 3.0L diesel options that move loads, families and miles — without breaking a sweat.",
    points: ["2.0L / 3.0L diesel", "High torque & efficiency", "Automatic & manual gearbox"],
    img: IMG.suv,
  },
  {
    id: "all-terrain",
    num: "02",
    title: "All Terrain Capability",
    hindi: "हर मोड़, हर मौसम",
    body: "City, highway, mountain, village — RWD/AWD and 4x4 options keep every road within reach. कहीं भी, कभी भी ताकत साथ है।",
    points: ["City | Highway | Mountain | Village", "RWD / AWD / 4x4", "All weather, all roads"],
    img: IMG.offroad,
  },
  {
    id: "safety",
    num: "03",
    title: "Advanced Safety",
    hindi: "सुरक्षा पहले",
    body: "360° protection with ADAS and advanced safety systems — watching every angle, so you can enjoy every drive.",
    points: ["360° protection", "ADAS & advanced systems", "Built for Indian roads"],
    img: IMG.hero,
  },
  {
    id: "ai-drive",
    num: "04",
    title: "AI Drive Assist",
    hindi: "स्मार्ट और सुरक्षित ड्राइविंग",
    body: "Smart and safe driving — AI Drive Assist watches the road, reads traffic and helps you steer, brake and hold your lane in real time.",
    points: ["Smart & safe driving", "Real-time traffic reading", "Lane keeping support"],
    img: IMG.cockpit,
  },
  {
    id: "connectivity",
    num: "05",
    title: "Smart Connectivity",
    hindi: "हमेशा जुड़े रहिए",
    body: "App, navigation and remote control — lock, locate and start your IshuCar from anywhere. Always in touch with your ride.",
    points: ["App, navigation, remote control", "Wireless Apple CarPlay | Android Auto", "Live vehicle status"],
    img: IMG.detail,
  },
  {
    id: "comfort",
    num: "06",
    title: "Premium Comfort",
    hindi: "आराम हर सफ़र में",
    body: "Spacious 7-seater cabin with ventilated seats front & rear, panoramic roof and flexible seating — more space, more life.",
    points: ["Spacious cabin | 7 seater", "Ventilated seats front & rear", "Panoramic roof"],
    img: IMG.highway,
  },
  {
    id: "interior",
    num: "07",
    title: "Next-Gen Interior",
    hindi: "टेक्नोलॉजी मिलती है लग्ज़री से",
    body: "Dual / triple digital cockpit screens, 64-color ambient lighting and premium leather finish — a cabin that feels like a lounge.",
    points: ["Digital cockpit — dual / triple screen", "Ambient lighting — 64 colors", "Premium material, leather finish"],
    img: IMG.interior,
  },
  {
    id: "economy",
    num: "08",
    title: "Fuel Efficient · BS6 Phase 2",
    hindi: "ज़्यादा किलोमीटर, कम खर्च",
    body: "More km, lower cost. Low maintenance and BS6 Phase 2 clean engine — responsible power, built for Indian roads.",
    points: ["More KM, lower cost", "Low maintenance", "BS6 Phase 2 — clean & responsible"],
    img: IMG.rear,
  },
  {
    id: "auto-drive",
    num: "09",
    title: "Auto Drive Mode",
    hindi: "आप relax, car खुद चले",
    body: "Highway पर Auto Drive on कीजिए — steering, speed और lane control car खुद संभालती है। Traffic में भी assist हमेशा साथ।",
    points: ["Auto steering + lane keeping", "Adaptive speed control", "Traffic assist हमेशा साथ"],
    img: IMG.cockpit,
  },
  {
    id: "servicing",
    num: "10",
    title: "₹300 Lifetime Servicing",
    hindi: "हर service सिर्फ ₹300 — lifetime",
    body: "सभी IshuCar models के लिए lifetime — हर service सिर्फ ₹300। Fixed price, no hidden charges — ownership का सबसे हल्का खर्चा।",
    points: ["₹300 per service — lifetime", "सभी models के लिए", "Fixed price — no hidden charges"],
    img: IMG.suv,
  },
];

function FeatureRow({ s, index }) {
  const flip = index % 2 === 1;
  return (
    <section className="py-20 lg:py-28" data-testid={`feature-section-${s.id}`}>
      <div className={`mx-auto grid max-w-7xl items-center gap-12 px-5 sm:px-8 lg:grid-cols-2 lg:gap-16`}>
        <Reveal className={flip ? "lg:order-2" : ""}>
          <p className="font-mono2 text-[12px] tracking-[0.3em] text-[#e6b800]">{s.num}</p>
          <h2 className="mt-4 font-display text-2xl font-bold uppercase tracking-tight text-white sm:text-3xl lg:text-4xl">
            {s.title}
          </h2>
          <p className="mt-5 max-w-lg text-base leading-relaxed text-neutral-400 sm:text-lg">{s.body}</p>
          <ul className="mt-7 space-y-3">
            {s.points.map((p) => (
              <li key={p} className="flex items-center gap-3 text-sm text-neutral-300">
                <span className="flex h-5 w-5 items-center justify-center rounded-full border border-[#e6b800]/40 bg-[#e6b800]/10 text-[#e6b800]">
                  <Check size={11} />
                </span>
                {p}
              </li>
            ))}
          </ul>
        </Reveal>
        <Reveal className={flip ? "lg:order-1" : ""} delay={0.1}>
          <motion.div
            whileHover={{ scale: 0.985 }}
            transition={{ duration: 0.4, ease: EASE }}
            className="tick-frame overflow-hidden rounded-2xl border border-white/10"
            data-testid={`feature-image-${s.id}`}
          >
            <img src={s.img} alt={s.title} loading="lazy" className="h-72 w-full object-cover sm:h-96" />
          </motion.div>
        </Reveal>
      </div>
    </section>
  );
}

export default function Features() {
  return (
    <>
      <section className="relative overflow-hidden pt-36 pb-16 sm:pb-20" data-testid="features-hero">
        <div className="bg-grid absolute inset-0 [mask-image:radial-gradient(ellipse_at_top,black_20%,transparent_75%)]" aria-hidden="true" />
        <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
          <SectionHeading
            eyebrow="The Machine"
            title="Engineering that thinks"
            sub="Eight systems, one strong diesel heart — built to assist, connect and comfort. यही है नई सोच, हर रास्ते के लिए।"
          />
        </div>
      </section>

      <div className="border-t border-white/10 bg-[#0a0a0e]/40">
        {SECTIONS.map((s, i) => (
          <FeatureRow key={s.id} s={s} index={i} />
        ))}
      </div>

      <section className="mx-auto max-w-4xl px-5 py-24 text-center sm:px-8" data-testid="features-cta">
        <Reveal>
          <h2 className="font-display text-3xl font-extrabold uppercase tracking-tight text-white sm:text-4xl">
            Words are one thing. <span className="text-gold-gradient">The drive is another.</span>
          </h2>
          <Link
            to="/booking"
            data-testid="features-reserve-button"
            className="group mt-9 inline-flex items-center gap-2 rounded-full bg-[#e6b800] px-8 py-4 font-bold text-[#070709] transition-all duration-200 hover:bg-[#ffc700] hover:shadow-[0_0_40px_rgba(230,184,0,0.5)]"
          >
            Reserve for ₹10,000 — fully refundable
            <ArrowRight size={17} className="transition-transform duration-200 group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </section>
    </>
  );
}